/**
 * @fileoverview Validador de coordenadas contra límites municipales
 * @module services/validation/MunicipalBoundaryValidator
 * @version 1.0.0
 * @task B03
 * 
 * Verifica que las coordenadas geocodificadas estén dentro del
 * polígono municipal correspondiente. Detecta casos donde CartoCiudad
 * devuelve INE correcto pero coordenadas de un barrio colindante.
 * 
 * Funcionalidades:
 * - Obtención de geometría municipal via WFS DERA
 * - Point-in-polygon con Turf.js
 * - Cálculo de distancia al borde más cercano
 * - Generación de OutOfBoundsError tipado
 * 
 * @example
 * ```typescript
 * const result = await validateWithinMunicipality(458000, 4128000, '18051');
 * if (!result.isValid) {
 *   console.log(`Fuera del límite: ${result.distanceOutside}m`);
 * }
 * ```
 */

import proj4 from 'proj4';
import distance from '@turf/distance';
import { point, polygon } from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import type { Polygon, MultiPolygon, Position } from 'geojson';
import { getBoundaryByINE, loadBoundaryByINE } from '../boundaries';
import {
  getBoundaryLoadStatus,
  type BoundaryLoadStatus,
} from '../boundaries/MunicipalBoundaryService';
import { getMunicipalityByINE } from './INEValidator';
import type { OutOfBoundsError, ErrorContext } from '../../types/errors/GeocodingErrors';
import type { MunicipalBoundary } from '../../lib/spatialValidation';
import type { InfrastructureType } from '../../lib/InfrastructureClassifier';
import { SUPRAMUNICIPAL_RADIUS_KM } from '../../lib/InfrastructureClassifier';
// B1a: enum del clasificador del orquestador (POLICIAL/BOMBEROS/EMERGENCIAS/…),
// distinto de la unión lib indexada por SUPRAMUNICIPAL_RADIUS_KM.
import { InfrastructureType as InfraEnum } from '../../types/infrastructure';
import { CENTROIDES_MUNICIPIOS } from '../../lib/municipiosCentroides';

// ============================================================================
// CONFIGURACIÓN PROJ4
// ============================================================================

// Asegurar definiciones EPSG
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs +type=crs');
}

// ============================================================================
// TIPOS
// ============================================================================

export interface BoundaryValidationResult {
  /** ¿Las coordenadas están dentro del municipio? */
  isValid: boolean;
  /** Coordenadas originales (UTM30) */
  coordinatesUTM: { x: number; y: number };
  /** Coordenadas convertidas (WGS84) */
  coordinatesWGS84: [number, number];
  /** Código INE del municipio */
  codigoINE: string;
  /** Nombre del municipio */
  municipio: string;
  /** Distancia al borde más cercano en metros (positivo = fuera) */
  distanceToBoundary: number;
  /** Error tipado si está fuera */
  error?: OutOfBoundsError;
  /** Mensaje descriptivo */
  message: string;
  /**
   * VALMUNI (DOP-2 b): presente solo si NO se pudo validar por falta de límite.
   * `isValid` sigue siendo `true` (no bloquea ni degrada), pero la fila queda
   * «sin validar» con su motivo: 404/snapshot ausente, fallo de red o no pedido
   * (`autoLoadBoundaries: false`).
   */
  sinLimite?: Exclude<BoundaryLoadStatus, 'cargado'>;
}

const MOTIVO_SIN_LIMITE: Record<Exclude<BoundaryLoadStatus, 'cargado'>, string> = {
  sin_limite: 'R2 no tiene su límite',
  error_red: 'fallo de red al cargarlo',
  no_pedido: 'no se ha cargado',
};

export interface BoundaryValidatorConfig {
  /** Tolerancia en metros para considerar "dentro" (default: 50) */
  toleranceMeters?: number;
  /** Cargar límites automáticamente si no están cargados */
  autoLoadBoundaries?: boolean;
  /** Timeout para carga de límites en ms */
  loadTimeout?: number;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/** Límites aproximados de Andalucía en UTM30 */
export const ANDALUSIA_BOUNDS_UTM = {
  minX: 100000,
  maxX: 620000,
  minY: 3980000,
  maxY: 4290000,
} as const;

/** Configuración por defecto */
const DEFAULT_CONFIG: Required<BoundaryValidatorConfig> = {
  toleranceMeters: 50,
  autoLoadBoundaries: true,
  loadTimeout: 30000,
};

// ============================================================================
// CACHÉ
// ============================================================================

/** Caché de conversiones UTM→WGS84 para evitar cálculos repetidos */
const coordinateCache = new Map<string, [number, number]>();
const CACHE_MAX_SIZE = 10000;

// ============================================================================
// FUNCIONES DE TRANSFORMACIÓN
// ============================================================================

/**
 * Convierte coordenadas UTM30 (EPSG:25830) a WGS84 (EPSG:4326)
 * @param x Coordenada X en UTM30
 * @param y Coordenada Y en UTM30
 * @returns [longitude, latitude] en WGS84
 */
export function utmToWgs84(x: number, y: number): [number, number] {
  const cacheKey = `${x.toFixed(0)},${y.toFixed(0)}`;
  
  const cached = coordinateCache.get(cacheKey);
  if (cached) return cached;
  
  const result = proj4('EPSG:25830', 'EPSG:4326', [x, y]) as [number, number];
  
  // Mantener caché bajo control
  if (coordinateCache.size >= CACHE_MAX_SIZE) {
    const firstKey = coordinateCache.keys().next().value;
    if (firstKey) coordinateCache.delete(firstKey);
  }
  
  coordinateCache.set(cacheKey, result);
  return result;
}

/**
 * Convierte coordenadas WGS84 a UTM30
 * @param lon Longitud
 * @param lat Latitud
 * @returns [x, y] en UTM30
 */
export function wgs84ToUtm(lon: number, lat: number): [number, number] {
  return proj4('EPSG:4326', 'EPSG:25830', [lon, lat]) as [number, number];
}

// ============================================================================
// FUNCIONES DE VALIDACIÓN GEOMÉTRICA
// ============================================================================

/**
 * Verifica si un punto está dentro de un polígono municipal
 * @param lonLat Coordenadas [lon, lat] en WGS84
 * @param boundary Límite municipal con geometría
 * @returns true si está dentro, false si no
 */
export function isPointInBoundary(
  lonLat: [number, number],
  boundary: MunicipalBoundary
): boolean {
  try {
    const pt = point(lonLat);
    
    if (boundary.geometry.type === 'MultiPolygon') {
      for (const ring of boundary.geometry.coordinates) {
        const partPoly = polygon(ring as Position[][]);
        if (booleanPointInPolygon(pt, partPoly)) {
          return true;
        }
      }
      return false;
    }
    
    const poly = polygon(boundary.geometry.coordinates as Position[][]);
    return booleanPointInPolygon(pt, poly);
  } catch {
    return false;
  }
}

/**
 * Calcula la distancia mínima entre un punto y el borde de un polígono
 * @param lonLat Coordenadas [lon, lat] en WGS84
 * @param boundary Límite municipal con geometría
 * @returns Distancia en metros (negativo = dentro, positivo = fuera)
 */
export function calculateDistanceToBoundary(
  lonLat: [number, number],
  boundary: MunicipalBoundary
): number {
  const isInside = isPointInBoundary(lonLat, boundary);
  const pt = point(lonLat);
  
  // Extraer todos los vértices del polígono
  const vertices = extractPolygonVertices(boundary.geometry);
  
  if (vertices.length === 0) return 0;
  
  // Calcular distancia mínima a cualquier vértice (aproximación)
  // Para mayor precisión se podría calcular distancia a segmentos
  let minDistance = Infinity;
  
  for (const vertex of vertices) {
    const vertexPt = point(vertex);
    const dist = distance(pt, vertexPt, { units: 'meters' });
    if (dist < minDistance) {
      minDistance = dist;
    }
  }
  
  // Si está dentro, distancia negativa; si fuera, positiva
  return isInside ? -minDistance : minDistance;
}

/**
 * Extrae todos los vértices de un polígono o multipolígono
 */
function extractPolygonVertices(geometry: Polygon | MultiPolygon): Position[] {
  const vertices: Position[] = [];
  
  if (geometry.type === 'MultiPolygon') {
    for (const polygonCoords of geometry.coordinates) {
      for (const ring of polygonCoords) {
        vertices.push(...ring);
      }
    }
  } else {
    for (const ring of geometry.coordinates) {
      vertices.push(...ring);
    }
  }
  
  return vertices;
}

// ============================================================================
// FUNCIÓN PRINCIPAL DE VALIDACIÓN
// ============================================================================

/**
 * Valida que las coordenadas UTM30 estén dentro del municipio esperado
 * 
 * @param x Coordenada X en UTM30 (EPSG:25830)
 * @param y Coordenada Y en UTM30 (EPSG:25830)
 * @param expectedINE Código INE del municipio esperado (5 dígitos)
 * @param config Configuración opcional
 * @returns Resultado de validación con posible error
 * 
 * @example
 * ```typescript
 * // Validar coordenadas de Colomera (Granada)
 * const result = await validateWithinMunicipality(458000, 4128000, '18051');
 * 
 * if (!result.isValid && result.error) {
 *   console.log(`Error: ${result.error.message}`);
 *   console.log(`Distancia fuera: ${result.distanceToBoundary}m`);
 * }
 * ```
 */
export async function validateWithinMunicipality(
  x: number,
  y: number,
  expectedINE: string,
  config: BoundaryValidatorConfig = {}
): Promise<BoundaryValidationResult> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const normalizedINE = expectedINE.padStart(5, '0');
  
  // Obtener datos del municipio
  const municipality = getMunicipalityByINE(normalizedINE);
  const municipioName = municipality?.nombre ?? `INE ${normalizedINE}`;
  
  // Convertir a WGS84
  const wgs84 = utmToWgs84(x, y);
  
  // VALMUNI (DOP-1 b): cargar el límite de ESTE municipio si no está en caché.
  // La caché recuerda los fallos (404 / red) y comparte peticiones en curso, así
  // que no se repite la petición en cada fila. `loadBoundaryByINE` no lanza.
  let boundary = getBoundaryByINE(normalizedINE);
  if (!boundary && cfg.autoLoadBoundaries) {
    boundary = await loadBoundaryByINE(normalizedINE, { timeout: cfg.loadTimeout });
  }

  if (!boundary) {
    // DOP-2 b: «sin validar», visible y sin bloquear (HX-FALLBACK-ENMASCARA-BUG).
    const estado = getBoundaryLoadStatus(normalizedINE);
    const sinLimite = estado === 'cargado' ? 'no_pedido' : estado;
    return {
      isValid: true,
      coordinatesUTM: { x, y },
      coordinatesWGS84: wgs84,
      codigoINE: normalizedINE,
      municipio: municipioName,
      distanceToBoundary: 0,
      sinLimite,
      message: `Sin validar: límite municipal no encontrado para ${municipioName} (${normalizedINE}) — ${MOTIVO_SIN_LIMITE[sinLimite]}`,
    };
  }
  
  // Verificar si el punto está dentro
  const isInside = isPointInBoundary(wgs84, boundary);
  const distanceMeters = calculateDistanceToBoundary(wgs84, boundary);
  
  // Aplicar tolerancia
  const effectivelyInside = isInside || Math.abs(distanceMeters) <= cfg.toleranceMeters;
  
  if (effectivelyInside) {
    return {
      isValid: true,
      coordinatesUTM: { x, y },
      coordinatesWGS84: wgs84,
      codigoINE: normalizedINE,
      municipio: municipioName,
      distanceToBoundary: distanceMeters,
      message: `Coordenadas válidas dentro de ${municipioName}`,
    };
  }
  
  // Generar error OutOfBoundsError
  const error = createOutOfBoundsError(x, y, normalizedINE, municipioName, distanceMeters);
  
  return {
    isValid: false,
    coordinatesUTM: { x, y },
    coordinatesWGS84: wgs84,
    codigoINE: normalizedINE,
    municipio: municipioName,
    distanceToBoundary: distanceMeters,
    error,
    message: error.message,
  };
}

// ============================================================================
// GENERACIÓN DE ERRORES
// ============================================================================

/**
 * Crea un OutOfBoundsError estructurado
 */
function createOutOfBoundsError(
  x: number,
  y: number,
  codigoINE: string,
  municipio: string,
  distanceOutside: number,
  context?: Partial<ErrorContext>
): OutOfBoundsError {
  // Determinar si podría ser huso UTM incorrecto
  const possibleWrongZone = x < 200000 || x > 500000;
  
  return {
    kind: 'out_of_bounds',
    severity: 'degraded',
    context: {
      timestamp: new Date(),
      source: context?.source ?? 'boundary_validator',
      level: context?.level ?? 'L1',
      attemptNumber: context?.attemptNumber ?? 1,
      municipio,
      codigoINE,
      ...context,
    } as ErrorContext,
    coordinate: { x, y },
    expectedBounds: ANDALUSIA_BOUNDS_UTM,
    distanceOutside: Math.round(distanceOutside),
    possibleWrongZone,
    message: buildOutOfBoundsMessage(x, y, municipio, codigoINE, distanceOutside),
  };
}

/**
 * Construye mensaje descriptivo para OutOfBoundsError
 */
function buildOutOfBoundsMessage(
  x: number,
  y: number,
  municipio: string,
  codigoINE: string,
  distanceOutside: number
): string {
  const distanceKm = (distanceOutside / 1000).toFixed(2);
  const distanceStr = distanceOutside >= 1000 
    ? `${distanceKm} km` 
    : `${Math.round(distanceOutside)} m`;
  
  return `Coordenadas (${x.toFixed(0)}, ${y.toFixed(0)}) fuera del límite de ${municipio} (${codigoINE}) — distancia: ${distanceStr}`;
}

// ============================================================================
// F9 — VALIDACIÓN SUPRAMUNICIPAL
// ============================================================================

/**
 * Resultado de la validación supramunicipal.
 * Permite distinguir infraestructuras que están fuera del BBox municipal
 * pero son legítimamente supramunicipales (bomberos comarcales, hospitales
 * provinciales, EDAR mancomunadas, etc.).
 */
export interface SupramunicipalValidationResult {
  /** Score de validación (0-100) */
  score: number;
  /** Distancia al centroide municipal en km */
  distanceKm: number;
  /** Radio máximo permitido para esta tipología en km */
  maxRadiusKm: number;
  /** ¿La infraestructura es supramunicipal? (fuera del BBox pero válida) */
  isSupramunicipal: boolean;
  /** Tipo de infraestructura evaluado */
  infrastructureType: InfrastructureType;
  /** Código INE del municipio */
  codigoINE: string;
  /** Razón del resultado */
  reason: 'inside_bbox' | 'supramunicipal_valid' | 'outside_radius' | 'no_centroid' | 'unknown_type';
}

/**
 * B1a — Mapea el `enum InfrastructureType` (clasificador del orquestador:
 * `POLICIAL`, `BOMBEROS`, `EMERGENCIAS`, `VIAL`, `PUERTO`, `MAREMOTO`, `RSU`,
 * `COMBUSTIBLE`, `GENERICO`…) a la unión de tipologías que indexa
 * `SUPRAMUNICIPAL_RADIUS_KM` (`SEGURIDAD`, `TRANSPORTE`, `TELECOMUNICACIONES`,
 * `SERVICIOS`, `OTROS`…).
 *
 * Sin este mapeo, un *cast* directo del enum produciría `radius = undefined`
 * para ~12 de 20 miembros y `validateSupramunicipal()` clasificaría en silencio
 * como `outside_radius` justo los tipos supramunicipales prototípicos (SEGURIDAD:
 * bomberos/emergencias comarcales) — `HX-FALLBACK-ENMASCARA-BUG`.
 *
 * Función total: el `Record` obliga a cubrir todos los miembros del enum (error
 * de compilación si se añade uno nuevo sin asignarle bucket).
 *
 * Decisiones de bucket ratificadas (B1a):
 *   POLICE·FIRE·EMERGENCY → SEGURIDAD (colapso comarcal, radio 45 km)
 *   VIAL·PORT             → TRANSPORTE
 *   TELECOM               → TELECOMUNICACIONES
 *   MUNICIPAL·SOCIAL      → SERVICIOS
 *   FUEL·MAREMOTO·RSU·GENERIC → OTROS
 */
const ENUM_TO_SUPRAMUNICIPAL: Readonly<Record<InfraEnum, InfrastructureType>> = {
  [InfraEnum.HEALTH]: 'SANITARIO',
  [InfraEnum.EDUCATION]: 'EDUCATIVO',
  [InfraEnum.POLICE]: 'SEGURIDAD',
  [InfraEnum.FIRE]: 'SEGURIDAD',
  [InfraEnum.EMERGENCY]: 'SEGURIDAD',
  [InfraEnum.CULTURAL]: 'CULTURAL',
  [InfraEnum.RELIGIOUS]: 'RELIGIOSO',
  [InfraEnum.SPORTS]: 'DEPORTIVO',
  [InfraEnum.MUNICIPAL]: 'SERVICIOS',
  [InfraEnum.SOCIAL]: 'SERVICIOS',
  [InfraEnum.FUEL]: 'OTROS',
  [InfraEnum.MAREMOTO]: 'OTROS',
  [InfraEnum.HYDRAULIC]: 'HIDRAULICO',
  [InfraEnum.ENERGY]: 'ENERGIA',
  [InfraEnum.TELECOM]: 'TELECOMUNICACIONES',
  [InfraEnum.VIAL]: 'TRANSPORTE',
  [InfraEnum.INDUSTRIAL]: 'INDUSTRIAL',
  [InfraEnum.RSU]: 'OTROS',
  [InfraEnum.PORT]: 'TRANSPORTE',
  [InfraEnum.GENERIC]: 'OTROS',
};

/**
 * Devuelve la tipología supramunicipal (unión lib) correspondiente a un valor
 * del `enum InfrastructureType`. Consumido por el cableado del scoring (B1b)
 * antes de invocar `validateSupramunicipal()`.
 */
export function mapInfraEnumToSupramunicipalType(
  type: InfraEnum,
): InfrastructureType {
  return ENUM_TO_SUPRAMUNICIPAL[type];
}

/**
 * Valida si una coordenada fuera del BBox municipal es legítimamente
 * supramunicipal según la tipología de infraestructura.
 *
 * **Algoritmo:**
 * 1. Obtiene centroide del municipio desde CENTROIDES_MUNICIPIOS (UTM30)
 * 2. Calcula distancia euclidiana punto → centroide (en metros, ÷1000 → km)
 * 3. Compara contra radio máximo según tipología (SUPRAMUNICIPAL_RADIUS_KM)
 * 4. Score degradado lineal: 95 (muy cerca) → 70 (en el límite del radio)
 *
 * @param x Coordenada X en UTM30 (EPSG:25830)
 * @param y Coordenada Y en UTM30 (EPSG:25830)
 * @param codigoINE Código INE del municipio del PTEL (5 dígitos)
 * @param infrastructureType Tipología clasificada de la infraestructura
 * @returns Resultado con score, distancia y flag supramunicipal
 *
 * @example
 * ```typescript
 * // Bomberos comarcal de Pinos Puente para PTEL Colomera (~15 km)
 * const result = validateSupramunicipal(432570, 4126000, '18051', 'SEGURIDAD');
 * // → { score: 83, isSupramunicipal: true, reason: 'supramunicipal_valid' }
 * ```
 *
 * @see .ptel/INV_INFRA_SUPRAMUNICIPAL.md
 */
export function validateSupramunicipal(
  x: number,
  y: number,
  codigoINE: string,
  infrastructureType: InfrastructureType
): SupramunicipalValidationResult {
  const normalizedINE = codigoINE.padStart(5, '0');
  const maxRadiusKm: number | undefined = SUPRAMUNICIPAL_RADIUS_KM[infrastructureType];

  // Tipología sin radio definido (no es clave de SUPRAMUNICIPAL_RADIUS_KM): no
  // enmascarar como outside_radius silencioso (HX-FALLBACK-ENMASCARA-BUG) —
  // señalarlo explícitamente. El mapeo enum→unión (mapInfraEnumToSupramunicipalType)
  // previene esto en la vía productiva; este guard es defensa en profundidad.
  if (maxRadiusKm === undefined) {
    return {
      score: 50,
      distanceKm: 0,
      maxRadiusKm: 0,
      isSupramunicipal: false,
      infrastructureType,
      codigoINE: normalizedINE,
      reason: 'unknown_type',
    };
  }

  // Obtener centroide del municipio (UTM30)
  const centroid = CENTROIDES_MUNICIPIOS[normalizedINE];

  if (!centroid) {
    return {
      score: 50,
      distanceKm: 0,
      maxRadiusKm,
      isSupramunicipal: false,
      infrastructureType,
      codigoINE: normalizedINE,
      reason: 'no_centroid',
    };
  }

  // Distancia euclidiana en UTM (metros) → km
  const dx = x - centroid.x;
  const dy = y - centroid.y;
  const distanceM = Math.sqrt(dx * dx + dy * dy);
  const distanceKm = distanceM / 1000;

  // Dentro del radio → score degradado linealmente de 95 a 70
  if (distanceKm <= maxRadiusKm) {
    // Ratio 0 (en el centroide) → 1 (en el borde del radio)
    const ratio = maxRadiusKm > 0 ? distanceKm / maxRadiusKm : 0;
    // Degradación lineal: 95 → 70 (25 puntos de rango)
    const score = Math.round(95 - ratio * 25);

    return {
      score,
      distanceKm: Math.round(distanceKm * 100) / 100,
      maxRadiusKm,
      isSupramunicipal: distanceKm > 0,
      infrastructureType,
      codigoINE: normalizedINE,
      reason: 'supramunicipal_valid',
    };
  }

  // Fuera del radio → probable error
  return {
    score: 20,
    distanceKm: Math.round(distanceKm * 100) / 100,
    maxRadiusKm,
    isSupramunicipal: false,
    infrastructureType,
    codigoINE: normalizedINE,
    reason: 'outside_radius',
  };
}

// ============================================================================
// PUERTA DE CONTENCIÓN MUNICIPAL (coordenada final · GATE-P1-S1)
// ============================================================================

/**
 * Umbral de rechazo en metros. Espejo de
 * `orchestrator/spatialValidation.ts::DISTANCE_THRESHOLD_REJECT`: fuera del
 * término por ≥ este valor → bloqueo (salvo buffer supramunicipal).
 */
export const CONTAINMENT_REJECT_M = 500;

/**
 * Umbral de degradación en metros. Espejo de
 * `orchestrator/spatialValidation.ts::DISTANCE_THRESHOLD_DEGRADE`: entre este
 * valor y el de rechazo → degradación de score, sin bloqueo.
 */
export const CONTAINMENT_DEGRADE_M = 100;

/** Acción que dicta la puerta de contención sobre la coordenada final. */
export type ContainmentAction = 'pass' | 'degrade' | 'block';

/** Resultado de la puerta de contención municipal. */
export interface ContainmentGateResult {
  /** Acción a aplicar: aceptar, degradar score o bloquear a revisión. */
  action: ContainmentAction;
  /** Distancia al borde del término (m, >0 fuera). null si no se pudo evaluar. */
  distanceToBoundary: number | null;
  /** true si una infra supramunicipal dentro de su radio evitó el bloqueo. */
  supramunicipalBuffered: boolean;
  /** Código INE normalizado evaluado. */
  codigoINE: string;
  /** Mensaje descriptivo (apto para el array `errores` del pipeline). */
  message: string;
}

/**
 * Puerta de contención municipal sobre la COORDENADA FINAL, agnóstica de
 * procedencia (documento o geocodificada). Reusa `validateWithinMunicipality`
 * (point-in-polygon Turf) y, para infra supramunicipal, `validateSupramunicipal`
 * (buffer por tipología). NO reinventa geometría: solo orquesta la decisión.
 *
 * Política (espejo de `orchestrator/spatialValidation.ts`):
 *   - distancia ≥ `CONTAINMENT_REJECT_M`        → `'block'`   (salvo buffer supra)
 *   - `CONTAINMENT_DEGRADE_M` ≤ d < rechazo      → `'degrade'`
 *   - d < `CONTAINMENT_DEGRADE_M` (tol. 50 m)     → `'pass'`
 *
 * El fallback blando de `validateWithinMunicipality` (límite no disponible →
 * `isValid:true` con `sinLimite`, VALMUNI) se propaga como `'pass'` con su
 * mensaje «Sin validar…»: sin límite no se puede contener. Caso fuera del
 * alcance de GATE-P1-S1 (es Puerta 0); se documenta, no se enmascara.
 *
 * @param x Coordenada X final (UTM30 / EPSG:25830).
 * @param y Coordenada Y final (UTM30 / EPSG:25830).
 * @param expectedINE Código INE del municipio del PTEL.
 * @param infraEnum Tipología (`InfraEnum`) para el buffer supramunicipal; `null` = sin buffer.
 * @param config Config. opcional de tolerancia / carga de límites.
 */
export async function evaluateMunicipalContainment(
  x: number,
  y: number,
  expectedINE: string,
  infraEnum: InfraEnum | null,
  config: BoundaryValidatorConfig = {},
): Promise<ContainmentGateResult> {
  const boundary = await validateWithinMunicipality(x, y, expectedINE, config);
  const codigoINE = boundary.codigoINE;

  // Dentro del término (o dentro de tolerancia, o sin límite cargado) → acepta.
  if (boundary.isValid) {
    return {
      action: 'pass',
      distanceToBoundary: boundary.distanceToBoundary,
      supramunicipalBuffered: false,
      codigoINE,
      message: boundary.message,
    };
  }

  const distM = boundary.distanceToBoundary;

  if (distM >= CONTAINMENT_REJECT_M) {
    // Buffer supramunicipal: una infra legítimamente supramunicipal (carretera,
    // río, línea eléctrica, etc.) dentro de su radio por tipología NO se bloquea.
    // El buffer exonera SOLO del bloqueo (no de la degradación), coherente con
    // el SPEC («…antes de bloquear») y con la política del orquestador.
    if (infraEnum !== null) {
      const supra = validateSupramunicipal(
        x,
        y,
        codigoINE,
        mapInfraEnumToSupramunicipalType(infraEnum),
      );
      if (supra.isSupramunicipal && supra.reason === 'supramunicipal_valid') {
        return {
          action: 'pass',
          distanceToBoundary: distM,
          supramunicipalBuffered: true,
          codigoINE,
          message: `Supramunicipal dentro de radio (${supra.distanceKm} km ≤ ${supra.maxRadiusKm} km)`,
        };
      }
    }
    return {
      action: 'block',
      distanceToBoundary: distM,
      supramunicipalBuffered: false,
      codigoINE,
      message: `Coordenada final fuera del término (${distM.toFixed(0)} m ≥ ${CONTAINMENT_REJECT_M} m)`,
    };
  }

  if (distM >= CONTAINMENT_DEGRADE_M) {
    return {
      action: 'degrade',
      distanceToBoundary: distM,
      supramunicipalBuffered: false,
      codigoINE,
      message: `Coordenada final cerca del borde del término (${distM.toFixed(0)} m)`,
    };
  }

  return {
    action: 'pass',
    distanceToBoundary: distM,
    supramunicipalBuffered: false,
    codigoINE,
    message: boundary.message,
  };
}

// ============================================================================
// UTILIDADES
// ============================================================================

/**
 * Limpia la caché de coordenadas (útil para tests)
 */
export function clearCoordinateCache(): void {
  coordinateCache.clear();
}

/**
 * Obtiene estadísticas de la caché
 */
export function getCacheStats(): { size: number; maxSize: number } {
  return {
    size: coordinateCache.size,
    maxSize: CACHE_MAX_SIZE,
  };
}

// ============================================================================
// EXPORTACIÓN POR DEFECTO
// ============================================================================

export default {
  validateWithinMunicipality,
  validateSupramunicipal,
  evaluateMunicipalContainment,
  isPointInBoundary,
  calculateDistanceToBoundary,
  utmToWgs84,
  wgs84ToUtm,
  clearCoordinateCache,
  getCacheStats,
  ANDALUSIA_BOUNDS_UTM,
};
